import { useState, useCallback, useRef } from 'react';

const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

const getAuthToken = (propToken) => {
  if (propToken) return propToken;
  try {
    const raw = localStorage.getItem('app_auth_session');
    if (!raw) return '';
    const p = JSON.parse(raw);
    return typeof p?.session_token === 'string' ? p.session_token.trim() : '';
  } catch { return ''; }
};
const authHeaders = (propToken) => {
  const h = { 'Content-Type': 'application/json' };
  const t = getAuthToken(propToken);
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
};

export function useImageRegeneration(templateId,token) {
  const [regen, setRegen] = useState(null);
  // { originalUrl, newUrl, promptHint, surroundingContext, status, error }
  // status: 'loading' | 'preview' | 'accepting' | 'error'

  const lastParamsRef = useRef(null);

  const _doFetch = useCallback(async (params) => {
  const { originalUrl, promptHint, surroundingContext, model, quality, aspectRatio } = params;

  setRegen(prev => ({
    ...(prev || {}),
    originalUrl,
    promptHint,
    surroundingContext,
    model,
    quality,
    aspectRatio,
    newUrl: null,
    status: 'loading',
    error: null,
  }));

  try {
    // ✅ Fetch old image and convert to base64 to send as reference
    let currentImageBase64 = null;
    try {
      const API_BASE = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
      const fullImageUrl = originalUrl.startsWith('http') 
        ? originalUrl 
        : `${API_BASE}${originalUrl.split('?')[0]}`; // strip cache-bust query params
      const imgRes = await fetch(fullImageUrl, { credentials: 'include' });

      if (!imgRes.ok || !imgRes.headers.get('content-type')?.startsWith('image/')) {
        console.warn('[useImageRegeneration] Image fetch failed or returned non-image, skipping base64');
        currentImageBase64 = null;
      } else {
        const blob = await imgRes.blob();
        currentImageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
        });
      }

    } catch (imgErr) {
      console.warn('[useImageRegeneration] Could not fetch old image as base64:', imgErr);
      // non-fatal — proceed without it, backend will do fresh generation
    }

    const res = await fetch(
      `${API_URL}/lesson/templates/${templateId}/regenerate-image`,
      {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(token),
        body: JSON.stringify({
          template: templateId,
          current_image_url: originalUrl,
          current_image_base64: currentImageBase64,  // ✅ new field
          prompt_hint: promptHint || '',
          surrounding_context: surroundingContext || '',
          model: model || 'openai',
          quality: quality || 'low',
          aspect_ratio: aspectRatio || '3:2',
        }),
      }
    );
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || `Error ${res.status}`);
    setRegen(prev => ({ ...prev, newUrl: data.new_image_url, status: 'preview', error: null }));
  } catch (err) {
    setRegen(prev => ({ ...prev, status: 'error', error: err.message }));
  }
}, [templateId]);

  // Start a new regeneration
  const start = useCallback((originalUrl, promptHint, surroundingContext, opts = {}) => {
  const params = {
    originalUrl,
    promptHint,
    surroundingContext,
    model: opts.model || 'openai',
    quality: opts.quality || 'low',
    aspectRatio: opts.aspectRatio || '3:2',
  };
  lastParamsRef.current = params;
  setRegen({
    ...params,
    newUrl: null,
    status: 'idle',
    error: null,
  });
}, []);

  // Retry with optional new prompt
  const retry = useCallback((overridePrompt) => {
    if (!lastParamsRef.current) return;
    const params = overridePrompt !== undefined
      ? { ...lastParamsRef.current, promptHint: overridePrompt }
      : lastParamsRef.current;
    lastParamsRef.current = params;
    _doFetch(params);
  }, [_doFetch]);

  // Accept — commits to HTML, returns new URL or null
  const accept = useCallback(async () => {
    if (!regen?.newUrl || !regen?.originalUrl) return null;
    setRegen(prev => ({ ...prev, status: 'accepting' }));
    try {
      const res = await fetch(
        `${API_URL}/lesson/templates/${templateId}/commit-image`,
        {
          method: 'POST',
          credentials: 'include',
          headers: authHeaders(token),
          body: JSON.stringify({
            template: templateId,
            old_image_url: regen.originalUrl,
            new_image_url: regen.newUrl,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `Error ${res.status}`);
      const committed = regen.newUrl;
      setRegen(null);
      return committed;
    } catch (err) {
      setRegen(prev => ({ ...prev, status: 'error', error: `Save failed: ${err.message}` }));
      return null;
    }
  }, [regen, templateId]);

  const cancel = useCallback(() => {
    setRegen(null);
    lastParamsRef.current = null;
  }, []);

  return { regen, start, retry, accept, cancel };
}
